import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"

/**
 * Card variants — all status-tinted variants use --mui-* token variables so
 * they automatically adapt between light and dark themes defined in index.css.
 */
const cardVariants = cva(
  "rounded-xl border bg-card text-card-foreground",
  {
    variants: {
      variant: {
        /** Default elevated card — standard layout surfaces. */
        default:
          "border-[hsl(var(--mui-border))] shadow-[var(--mui-shadow-sm)] dark:shadow-[var(--mui-shadow-md)]",
        /** Metric / KPI card — used for dashboard number tiles. */
        metric:
          "border-[hsl(var(--mui-border))] shadow-[var(--mui-shadow-sm)] dark:shadow-[var(--mui-shadow-md)]",
        /** Interactive card — pointer cursor + hover lift. */
        interactive:
          "border-[hsl(var(--mui-border))] shadow-[var(--mui-shadow-sm)] cursor-pointer transition-all duration-150 hover:shadow-[var(--mui-shadow-lg)] hover:-translate-y-0.5 hover:border-[hsl(var(--mui-border-strong))]",
        /** Warning tint — for alerts, low-stock notices, etc. */
        warning:
          "bg-[hsl(var(--mui-warning)/0.07)] border-[hsl(var(--mui-warning)/0.35)] shadow-none",
        /** Success tint — for positive states, confirmations. */
        success:
          "bg-[hsl(var(--mui-success)/0.07)] border-[hsl(var(--mui-success)/0.35)] shadow-none",
        /** Info tint — for informational notices. */
        info:
          "bg-[hsl(var(--mui-info)/0.07)] border-[hsl(var(--mui-info)/0.35)] shadow-none",
        /** Empty-state card — dashed border, no shadow. */
        empty:
          "border-dashed border-[hsl(var(--mui-border))] bg-[hsl(var(--mui-hover)/0.5)] shadow-none",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

/** Loading skeleton placeholder for a card. */
function CardSkeleton({
  className,
  lines = 3,
}: {
  className?: string
  lines?: number
}) {
  return (
    <Card className={cn("animate-pulse", className)}>
      <CardHeader>
        <Skeleton className="h-5 w-2/5 rounded-md" />
        <Skeleton className="h-3.5 w-3/5 rounded-md mt-1" />
      </CardHeader>
      <CardContent className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton
            key={i}
            className="h-3.5 w-full rounded-md"
            style={{ opacity: 1 - i * 0.2 }}
          />
        ))}
      </CardContent>
    </Card>
  )
}

/** Empty-state card — centred icon / title / description / action. */
function CardEmpty({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
  className?: string
}) {
  return (
    <Card
      variant="empty"
      className={cn(
        "flex flex-col items-center justify-center py-12 px-6 text-center",
        className
      )}
    >
      {icon && (
        <div className="mb-4 text-[hsl(var(--mui-text-muted))] [&_svg]:h-10 [&_svg]:w-10">
          {icon}
        </div>
      )}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description && (
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xs">
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </Card>
  )
}

/** Metric card — value + label + optional trend + comparison text. */
interface MetricCardProps {
  label: string
  value: React.ReactNode
  /** e.g. "+12.4%" */
  trend?: string
  /** positive = up (success), negative = down (danger), neutral = grey */
  trendDirection?: "positive" | "negative" | "neutral"
  comparisonText?: string
  /** When true, replaces value with a skeleton (e.g. no view_sale_price permission). */
  masked?: boolean
  className?: string
  children?: React.ReactNode
}

function MetricCard({
  label,
  value,
  trend,
  trendDirection = "neutral",
  comparisonText,
  masked = false,
  className,
  children,
}: MetricCardProps) {
  const trendColor =
    trendDirection === "positive"
      ? "text-[hsl(var(--mui-success))]"
      : trendDirection === "negative"
      ? "text-[hsl(var(--mui-danger))]"
      : "text-[hsl(var(--mui-text-muted))]"

  return (
    <Card variant="metric" className={cn("p-5", className)}>
      <p className="text-xs font-medium uppercase tracking-wide text-[hsl(var(--mui-text-muted))]">
        {label}
      </p>
      <div className="mt-2 flex items-baseline gap-2">
        {masked ? (
          <Skeleton className="h-7 w-24 rounded-md" />
        ) : (
          <span className="text-2xl font-bold tracking-tight text-foreground">
            {value}
          </span>
        )}
        {trend && !masked && (
          <span className={cn("text-xs font-medium", trendColor)}>{trend}</span>
        )}
      </div>
      {comparisonText && !masked && (
        <p className="mt-1 text-xs text-[hsl(var(--mui-text-muted))]">
          {comparisonText}
        </p>
      )}
      {children && <div className="mt-3">{children}</div>}
    </Card>
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardSkeleton,
  CardEmpty,
  MetricCard,
  cardVariants,
}
